import {
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { cn } from './cn.js';
import { folded } from './fold.js';
import { useRequiredMark } from './Form.js';
import { CompactTriggerButton, type CompactTrigger } from './CompactTrigger.js';
import { Icon } from './Icon.js';
import { Popover, PopoverContent, PopoverTrigger } from './primitives/popover.js';

export interface SearchSelectOption {
  value: string;
  label: string;
  /**
   * Um dado curto à direita da opção, em cinza e com algarismos alinhados — o
   * código do voluntário ao lado do nome (30/10/2026). Entra na busca local
   * junto com o rótulo.
   */
  aside?: string;
}

export interface SearchSelectProps {
  label: string;
  options: readonly SearchSelectOption[];
  /**
   * O `value` escolhido, ou `''` para nenhum. Sem ele o campo guarda o próprio
   * estado, a partir de `defaultValue` — é o que o formulário desenhado a partir
   * de dado (`ClaimField`) precisa, porque lê tudo por `FormData` no envio.
   */
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  /** Vai num `<input type="hidden">` com o `value`, para o `FormData` ler. */
  name?: string;
  /** O texto do gatilho quando nada está escolhido. */
  placeholder?: string;
  searchPlaceholder?: string;
  /** Quando a busca não acha nada dentro das opções. */
  emptyMessage?: string;
  help?: ReactNode;
  error?: string | null;
  required?: boolean;
  disabled?: boolean;
  /** Esconde o rótulo visualmente e o mantém para leitor de tela. */
  hiddenLabel?: boolean;
  /**
   * As opções ainda estão a caminho. "Nada encontrado" mentiria sobre uma
   * lista vazia por enquanto — o mesmo motivo do `MultiSelect`.
   */
  loading?: boolean;
  /**
   * Um botão sob a busca que alterna a ordem da lista: a de `options`, A → Z e
   * Z → A.
   *
   * Começa na ordem de `options`, e não em A → Z como o `MultiSelect`: aqui a
   * ordem de quem chama costuma ser informação — "Sim" antes de "Não", o
   * catálogo na ordem do módulo. Ordenar é escolha de quem procura, não um
   * padrão imposto a quem declarou a lista.
   *
   * Só aparece com mais de cinco opções, como a busca (ver `SHORT_LIST`).
   */
  sortable?: boolean;
  /**
   * Um X no campo, quando há opção escolhida, que volta para "nada escolhido".
   *
   * O `<select>` nativo não desfaz uma escolha: depois de marcar "Sim" num campo
   * opcional, voltar a "não sei" era impossível sem uma opção vazia fingindo ser
   * valor. Com o X, vazio é vazio.
   */
  clearable?: boolean;
  /**
   * Busca NO SERVIDOR: cada termo digitado chega aqui, e `options` passa a ser
   * a resposta dele — o componente para de filtrar por conta própria. Sem
   * isto, a busca filtra as opções que já estão na mão.
   *
   * Quem passa isto cuida do intervalo antes de ir ao servidor
   * (`useDebouncedTerm`, na interna), porque a pergunta é de quem conhece a
   * rota; o componente só relata o que foi digitado. Ao fechar, o termo volta
   * a `''`.
   */
  onSearchChange?: (term: string) => void;
  /**
   * O rótulo do que está escolhido, quando a escolha pode não estar em
   * `options`. Na busca no servidor, a lista muda a cada termo, e a opção
   * escolhida some dela — sem isto o gatilho voltaria ao `placeholder` com um
   * valor escolhido por baixo.
   */
  selectedLabel?: string;
  /**
   * Uma pastilha de ícone no lugar do campo (`CompactTrigger`) — o analista
   * ao lado do título da mesa. O rótulo vai para o leitor de tela; com
   * `clearable`, o X sai do gatilho e vira "Tirar" no pé da lista.
   */
  compact?: CompactTrigger;
}

type Order = 'original' | 'asc' | 'desc';

/**
 * Até quantas opções a lista se lê de relance — e aí não há busca nem ordem.
 *
 * Numa pergunta de "Sim / Não" ou de três destinos, uma caixa de busca e um
 * botão de ordenar acima de duas linhas são mais controle do que lista: a
 * pessoa lê as opções antes de achar onde digitar. Acima de cinco a lista
 * começa a pedir rolagem ou leitura, e os dois voltam. Vale para `sortable`
 * também — ordenar cinco itens não ajuda ninguém a achar nada.
 */
const SHORT_LIST = 5;

/* O próximo passo da ordem, e como o botão se anuncia em cada um. */
const NEXT_ORDER: Record<Order, Order> = {
  original: 'asc',
  asc: 'desc',
  desc: 'original',
};
const ORDER_LABEL: Record<Order, { short: string; long: string }> = {
  original: { short: 'Original', long: 'Na ordem original — trocar para A a Z' },
  asc: { short: 'A → Z', long: 'Ordenado de A a Z — trocar para Z a A' },
  desc: { short: 'Z → A', long: 'Ordenado de Z a A — voltar à ordem original' },
};

/**
 * Escolha de UMA opção entre muitas, com busca dentro da lista.
 *
 * ## Por que não é o `Select`
 *
 * O `Select` é um `<select>` nativo de propósito, e continua sendo o certo para
 * poucas opções curtas. Ele para de servir em dois casos, e o modal de exceções
 * de permissão tem os dois:
 *
 * - **A lista é longa**, e o nativo não tem busca. O catálogo tem dezenas de
 *   recursos, e achar um rolando a roda do sistema é trabalho de adivinhação.
 * - **O texto da opção é longo.** O rótulo de uma ação do catálogo é uma frase
 *   inteira, e o `<option>` não quebra linha: no telefone a roda corta a frase,
 *   e no computador a lista abre mais larga que o modal. Aqui a opção quebra
 *   linha dentro da largura do campo, e o gatilho mostra até duas linhas.
 *
 * É o `MultiSelect` para uma escolha só: a camada (abrir, fechar, devolver o
 * foco) é do `Popover`, e a busca, a lista e o teclado são nossos.
 *
 * ## Teclado
 *
 * O foco vai para a busca ao abrir. Seta para baixo e para cima andam pela
 * lista filtrada, Enter escolhe, Esc fecha (do `Popover`). A opção ativa é
 * anunciada por `aria-activedescendant`, sem tirar o foco do campo de busca —
 * quem digita não perde o que está digitando.
 *
 * ## `z-50`, e não o `z-10` do `MultiSelect`
 *
 * O `MultiSelect` abre dentro do painel de filtros, abaixo da faixa do topo. Este
 * nasceu para abrir DENTRO de modal, cujo véu é `z-50`: com `z-10` a lista
 * abriria por baixo dele. O portal monta depois do modal, e empate de `z`
 * desenha na ordem do DOM.
 */
export function SearchSelect({
  label,
  options,
  value: controlled,
  defaultValue = '',
  onChange,
  name,
  placeholder = 'Escolha…',
  searchPlaceholder = 'Buscar…',
  emptyMessage = 'Nada encontrado.',
  help,
  error,
  required,
  disabled = false,
  hiddenLabel = false,
  loading = false,
  sortable = false,
  clearable = false,
  onSearchChange,
  selectedLabel,
  compact,
}: SearchSelectProps) {
  const idLabel = useId();
  const idTrigger = useId();
  const idList = useId();
  const idHelp = useId();
  const idError = useId();
  const [inner, setInner] = useState(defaultValue);
  const value = controlled ?? inner;
  const publish = (next: string) => {
    if (controlled === undefined) setInner(next);
    onChange?.(next);
  };
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [active, setActive] = useState(0);
  const [order, setOrder] = useState<Order>('original');
  const searchRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Ver `Field`: o aviso de obrigatórios do rodapé é contado, não escrito.
  useRequiredMark(required);

  /*
   * A ordem é a de `options`, e não alfabética. Quem chama já ordena quando a
   * ordem importa — o catálogo vem na ordem em que cada módulo declarou, que
   * põe a ação de piso antes da que a alarga. Com `sortable`, quem procura
   * pode trocar; a ordem de `options` continua sendo o ponto de partida.
   */
  const remote = onSearchChange !== undefined;
  const visible = useMemo(() => {
    if (remote) return options;
    const needle = folded(term.trim());
    const found = remote
      ? [...options]
      : needle
        ? options.filter((o) => folded(`${o.label} ${o.aside ?? ''}`).includes(needle))
        : [...options];
    if (order === 'original') return found;
    const sorted = found.sort((a, b) =>
      a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' }),
    );
    return order === 'asc' ? sorted : sorted.reverse();
  }, [options, term, order, remote]);

  const chosen =
    options.find((o) => o.value === value) ??
    (value !== '' && selectedLabel !== undefined
      ? { value, label: selectedLabel }
      : undefined);
  /*
   * Na busca no servidor a lista é a resposta do termo, e pode vir curta: a
   * busca não pode sumir por isso, senão não há como digitar outro termo.
   */
  const searchable = remote || options.length > SHORT_LIST;
  const canClear = clearable && chosen !== undefined && !disabled;

  const search = (next: string) => {
    setTerm(next);
    onSearchChange?.(next);
  };

  const clear = () => {
    publish('');
    /* O X some junto com a escolha: o foco volta ao campo, e não ao nada. */
    triggerRef.current?.focus();
  };

  const choose = (option: SearchSelectOption) => {
    publish(option.value);
    setOpen(false);
    search('');
  };

  const move = (to: number) => {
    const next = Math.max(0, Math.min(visible.length - 1, to));
    setActive(next);
    listRef.current
      ?.querySelector(`[data-index="${next}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  };

  /* O mesmo teclado na busca e, na lista curta sem busca, na própria lista. */
  const onNavKey = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      move(active + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      move(active - 1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const option = visible[active];
      if (option) choose(option);
    }
  };

  const described = [help ? idHelp : null, error ? idError : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={compact ? 'inline-flex' : 'flex min-w-0 flex-col gap-1.5'}>
      <span
        id={idLabel}
        className={cn(
          'text-xs font-medium text-texto',
          (hiddenLabel || compact) && 'sr-only',
        )}
      >
        {label}
        {required && (
          <>
            <span aria-hidden="true" className="ml-0.5 text-erro">
              *
            </span>
            <span className="sr-only"> (obrigatório)</span>
          </>
        )}
      </span>

      {/*
       * `modal` é o que faz a lista ROLAR dentro de um modal.
       *
       * O Dialog trava o scroll de tudo o que está fora do conteúdo dele
       * (`react-remove-scroll`), e esta lista mora num portal — fora dele. Sem
       * `modal`, a roda do mouse e o arraste no telefone eram engolidos, e a
       * lista parecia sem rolagem. Com `modal`, o Popover registra a própria
       * camada de scroll por cima da do Dialog. O preço é o de um select: o
       * resto da tela não recebe clique enquanto a lista está aberta.
       */}
      <Popover
        modal
        open={open}
        onOpenChange={(next) => {
          if (disabled) return;
          setOpen(next);
          if (next) {
            /* Abre com a escolhida ativa, para Enter não trocar sem querer. */
            const at = visible.findIndex((o) => o.value === value);
            setActive(at < 0 ? 0 : at);
          } else {
            search('');
          }
        }}
      >
        {/*
         * O X mora AO LADO do gatilho, e não dentro: botão dentro de botão não é
         * HTML válido, e o clique nele abriria a lista em vez de limpar. Ele se
         * sobrepõe ao canto do gatilho por posição, e o gatilho reserva o espaço.
         */}
        {compact ? (
          <PopoverTrigger asChild>
            <CompactTriggerButton
              ref={triggerRef}
              compact={compact}
              id={idTrigger}
              disabled={disabled}
            />
          </PopoverTrigger>
        ) : (
          <div className="relative">
            <PopoverTrigger asChild>
              <button
                /* Veste um campo: o foco dele é o de dentro, não o anel do botão. */
                data-campo=""
                ref={triggerRef}
                type="button"
                id={idTrigger}
                disabled={disabled}
                aria-labelledby={`${idLabel} ${idTrigger}`}
                aria-describedby={described || undefined}
                aria-invalid={error ? true : undefined}
                /* A frase inteira no `title`: o gatilho corta na segunda linha. */
                title={chosen?.label}
                className={cn(
                  'flex min-h-9 w-full items-center justify-between gap-2 rounded-borda border bg-superficie px-3 py-1.5 text-left text-xs',
                  'text-texto pointer-coarse:min-h-11',
                  'transition-[border-color,background-color] active:bg-superficie-suave',
                  'disabled:cursor-not-allowed disabled:bg-superficie-suave disabled:opacity-60',
                  error ? 'border-erro' : 'border-borda-forte hover:border-texto-suave',
                  canClear && 'pr-16 pointer-coarse:pr-20',
                )}
              >
                <span
                  className={cn(
                    'line-clamp-2 min-w-0 break-words',
                    chosen === undefined && 'text-texto-suave',
                  )}
                >
                  {chosen?.label ?? placeholder}
                </span>
                <Icon nome="seta-baixo" size={16} className="shrink-0 text-texto-suave" />
              </button>
            </PopoverTrigger>
            {canClear && (
              <button
                type="button"
                onClick={clear}
                aria-label={`Limpar ${label}`}
                title="Limpar"
                className={cn(
                  'absolute right-8 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-borda text-texto-suave',
                  'hover:bg-superficie-suave hover:text-texto active:bg-borda',
                  'pointer-coarse:right-9 pointer-coarse:h-11 pointer-coarse:w-11',
                )}
              >
                <Icon nome="fechar" size={16} />
              </button>
            )}
          </div>
        )}

        <PopoverContent
          align="start"
          /*
           * A largura é a do gatilho, e nunca maior que a tela. É o que faz a
           * opção longa quebrar linha em vez de alargar o modal.
           */
          className={cn(
            compact ? 'w-[16rem]' : 'w-(--radix-popover-trigger-width)',
            'max-w-[calc(100vw-2rem)] p-0',
          )}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            (searchable ? searchRef.current : listRef.current)?.focus();
          }}
        >
          {searchable && (
            <div className="border-b border-borda p-2">
              <div className="relative">
                <Icon
                  nome="busca"
                  size={16}
                  className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-texto-suave"
                />
                <input
                  ref={searchRef}
                  type="text"
                  role="combobox"
                  aria-expanded={open}
                  aria-controls={idList}
                  aria-autocomplete="list"
                  aria-activedescendant={
                    visible[active] ? `${idList}-${active}` : undefined
                  }
                  aria-label={`Buscar em ${label}`}
                  value={term}
                  onChange={(e) => {
                    search(e.target.value);
                    setActive(0);
                  }}
                  onKeyDown={onNavKey}
                  placeholder={searchPlaceholder}
                  className={cn(
                    'h-9 w-full rounded-borda border border-borda-forte bg-superficie pl-8 pr-2 text-sm',
                    'text-texto placeholder:text-texto-suave pointer-coarse:h-11',
                  )}
                />
              </div>

              {sortable && (
                <div className="mt-2 flex items-center gap-1.5 text-xs">
                  <span className="text-texto-suave">Ordenar</span>
                  <button
                    type="button"
                    onClick={() => {
                      setOrder(NEXT_ORDER[order]);
                      setActive(0);
                    }}
                    aria-label={ORDER_LABEL[order].long}
                    title={ORDER_LABEL[order].long}
                    className="flex h-7 items-center gap-1 rounded-borda px-1.5 font-semibold text-texto-suave hover:bg-superficie-suave hover:text-texto active:bg-borda pointer-coarse:h-11"
                  >
                    <Icon
                      nome={
                        order === 'desc'
                          ? 'seta-baixo'
                          : order === 'asc'
                            ? 'seta-cima'
                            : 'ordenar'
                      }
                      size={16}
                    />
                    {ORDER_LABEL[order].short}
                  </button>
                </div>
              )}
            </div>
          )}

          <div
            ref={listRef}
            id={idList}
            role="listbox"
            aria-labelledby={idLabel}
            /* Sem busca, quem recebe o foco e o teclado é a lista. */
            {...(searchable
              ? {}
              : {
                  tabIndex: -1,
                  onKeyDown: onNavKey,
                  'aria-activedescendant': visible[active]
                    ? `${idList}-${active}`
                    : undefined,
                })}
            aria-busy={loading || undefined}
            className="max-h-64 overflow-y-auto p-1 outline-none"
          >
            {loading && (
              <p className="px-2 py-3 text-center text-sm text-texto-suave">
                Carregando…
              </p>
            )}
            {!loading && visible.length === 0 && (
              <p className="px-2 py-3 text-center text-sm text-texto-suave">
                {emptyMessage}
              </p>
            )}
            {!loading &&
              visible.map((option, index) => {
                const selected = option.value === value;
                return (
                  <div
                    key={option.value}
                    id={`${idList}-${index}`}
                    data-index={index}
                    role="option"
                    aria-selected={selected}
                    onMouseEnter={() => setActive(index)}
                    /*
                     * `onMouseDown` com `preventDefault` segura o foco na busca —
                     * sem ele o clique tira o foco do campo antes de escolher.
                     */
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => choose(option)}
                    className={cn(
                      'flex min-h-9 cursor-pointer items-start gap-2 rounded-borda px-2 py-2 text-sm leading-snug text-texto pointer-coarse:min-h-11',
                      index === active && 'bg-superficie-suave',
                    )}
                  >
                    <Icon
                      nome="confirmado"
                      size={16}
                      className={cn(
                        'mt-0.5 shrink-0 text-primaria-texto',
                        !selected && 'invisible',
                      )}
                    />
                    <span className="min-w-0 flex-1 break-words">{option.label}</span>
                    {option.aside !== undefined && (
                      <span className="shrink-0 tabular-nums text-texto-suave">
                        {option.aside}
                      </span>
                    )}
                  </div>
                );
              })}
          </div>
          {compact && canClear && (
            <div className="border-t border-borda p-1.5">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  clear();
                }}
                className="w-full rounded-borda px-2 py-1.5 text-center text-xs font-semibold text-texto-suave hover:bg-superficie-suave hover:text-texto"
              >
                Tirar {label.toLocaleLowerCase('pt-BR')}
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {name && <input type="hidden" name={name} value={value} />}

      {help && (
        <p id={idHelp} className="text-rotulo text-texto-suave">
          {help}
        </p>
      )}
      {error && (
        <p id={idError} className="flex items-center gap-1 text-xs font-medium text-erro">
          <Icon nome="atencao" size={16} />
          {error}
        </p>
      )}
    </div>
  );
}
